import { useState, useEffect, useSyncExternalStore, useCallback, type ReactNode } from 'react';
import { AuthContext } from './auth';
import { 
  loadGoogleScript, 
  verifyWithBackend,
  saveSession, 
  getSession, 
  logout as googleLogout,
  promptGoogleSignIn,
  type GoogleUser 
} from '../services/googleAuth';

// Store simple pour gérer l'état d'authentification
let currentUser: GoogleUser | null = null;
const listeners: Set<() => void> = new Set();

function subscribe(callback: () => void) {
  listeners.add(callback);
  return () => listeners.delete(callback);
}

function getSnapshot() {
  return currentUser;
}

function setAuthUser(user: GoogleUser | null) {
  currentUser = user;
  listeners.forEach(fn => fn());
}

export function AuthProvider({ children }: { children: ReactNode }) {
  // Initialiser avec la session sauvegardée (lazy initialization)
  const [isLoading] = useState(() => {
    const { user: savedUser } = getSession();
    if (savedUser) {
      currentUser = savedUser;
    }
    return false;
  });
  
  const [error, setError] = useState<string | null>(null);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  
  const user = useSyncExternalStore(subscribe, getSnapshot, () => null);

  // Charger le script Google au montage
  useEffect(() => {
    loadGoogleScript()
      .then(() => setIsScriptLoaded(true))
      .catch(() => setError('Erreur de chargement de Google Auth'));
  }, []);

  // Handler pour la réponse Google
  const handleGoogleResponse = useCallback(async (response: { credential: string }) => {
    setError(null);
    try {
      // Send token to backend for secure validation against ALLOWED_EMAIL
      const verifiedUser = await verifyWithBackend(response.credential);
      saveSession(response.credential, verifiedUser);
      setAuthUser(verifiedUser);
    } catch (err: unknown) {
      console.error("[AuthProvider] Authentication failed", err);
      setError(err instanceof Error ? err.message : 'Accès refusé.');
      setAuthUser(null);
    }
  }, []);

  const login = useCallback(async () => {
    if (!isScriptLoaded) {
      setError('Chargement de Google Auth en cours...');
      return;
    }

    // Ouvrir la popup Google
    console.log('[AuthProvider] Triggering Google prompt...');
    promptGoogleSignIn(handleGoogleResponse);
  }, [isScriptLoaded, handleGoogleResponse]);

  const logout = useCallback(() => {
    googleLogout();
    setAuthUser(null);
    setError(null);
  }, []);

  const isAuthenticated = !!user;
  const isAuthorizedUser = !!user; // if user is set, they are authorized by the backend

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isAuthorized: isAuthorizedUser,
        isLoading,
        login,
        logout,
        error,
        isScriptLoaded,
        handleGoogleResponse,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
